import { APPLICATION_CONFIG, AUTH_API_ROUTES } from "@constants/constants";
import { AUTH_EVENTS, authEmitter } from "@utils/auth-events";
import { tokenInstance } from "@utils/token-instance";
import type {
	AxiosInstance,
	AxiosRequestHeaders,
	InternalAxiosRequestConfig,
} from "axios";
import axios, { CanceledError } from "axios";

interface AuthResponse {
	active_token?: string | null;
	refresh_token?: string | null;
}

/** Marks a request that has already been retried once after a refresh. */
interface RetriableConfig extends InternalAxiosRequestConfig {
	isSessionRetry?: boolean;
}

// A rejected request kicks off a refresh, and every other request that fails
// while it is in flight has to await that same one: the gateway rotates the
// pair on every refresh, so a second call would spend a token the first has
// already replaced and kill the session it was meant to save.
let refreshInFlight: Promise<string | null> | null = null;

const requestNewTokenPair = async (): Promise<string | null> => {
	const refreshToken = tokenInstance.getRefreshToken();
	if (!refreshToken) return null;

	try {
		// Deliberately a bare client: the instrumented ones would re-enter this
		// same interceptor when the refresh itself is rejected.
		const { data } = await axios.post<AuthResponse>(
			`${APPLICATION_CONFIG.auth_url}${AUTH_API_ROUTES.REFRESH}`,
			undefined,
			{ headers: { Authorization: `Bearer ${refreshToken}` } },
		);

		if (!data?.active_token) return null;

		tokenInstance.setToken(data.active_token);
		if (data.refresh_token) tokenInstance.setRefreshToken(data.refresh_token);

		return data.active_token;
	} catch {
		// The pair is shared by every tab through localStorage, and the lock
		// above is per tab: another one may have rotated it first, which spends
		// the token this call sent. Its new pair is as good as ours.
		const current = tokenInstance.getRefreshToken();
		return current && current !== refreshToken
			? tokenInstance.getToken()
			: null;
	}
};

export const refreshSession = (): Promise<string | null> => {
	if (!refreshInFlight) {
		refreshInFlight = requestNewTokenPair().finally(() => {
			refreshInFlight = null;
		});
	}

	return refreshInFlight;
};

interface AuthInterceptorOptions {
	/** Requests that carry no session yet — login and the refresh call itself. */
	isPublic?: (config: InternalAxiosRequestConfig) => boolean;
}

/**
 * Bearer auth plus one refresh-and-retry on a 401, shared by every client that
 * talks to a service behind the Auth Gateway's tokens.
 */
export const attachAuthInterceptors = (
	instance: AxiosInstance,
	{ isPublic }: AuthInterceptorOptions = {},
) => {
	instance.interceptors.request.use(
		(config: InternalAxiosRequestConfig) => {
			if (isPublic?.(config)) return config;

			config.headers = {
				...config.headers,
				Authorization: `Bearer ${tokenInstance.getToken()}`,
			} as AxiosRequestHeaders;

			return config;
		},
		(error) => Promise.reject(error),
	);

	instance.interceptors.response.use(
		(response) => response,
		async (error) => {
			if (error instanceof CanceledError) return Promise.reject(error);

			const status = error?.response?.status ?? error?.status;
			const config = error?.config as RetriableConfig | undefined;

			if (status !== 401) return Promise.reject(error);

			if (config && !config.isSessionRetry && !isPublic?.(config)) {
				// Another tab may already have rotated the shared pair since this
				// request left; refreshing again would replay a spent token.
				const stored = tokenInstance.getToken();
				const token =
					stored && config.headers?.Authorization !== `Bearer ${stored}`
						? stored
						: await refreshSession();

				if (token) {
					config.isSessionRetry = true;
					config.headers = {
						...config.headers,
						Authorization: `Bearer ${token}`,
					} as AxiosRequestHeaders;

					return instance.request(config);
				}
			}

			authEmitter.emit(AUTH_EVENTS.UNAUTHORIZED);

			return Promise.reject(error);
		},
	);
};
